import { Link } from "@tanstack/react-router";
import { hubForSeoTrade, otherHubs, relatedAnswersFor } from "@/lib/internal-links";

interface Props {
  /** SEO matrix or programmatic trade slug, e.g. "plumbing", "hvac", "drain-cleaning". */
  seoTrade: string;
  /** City label used in link copy, e.g. "Lehi". */
  cityLabel: string;
  /** Free-text focus for answer relevance (problem label, service term, etc.). */
  focusText: string;
  /** Optional heading tone. */
  heading?: string;
}

/**
 * Automatic internal-link block: every /seo/* page cross-links to the
 * matching /hubs/{trade} page, sibling hubs, and relevance-scored
 * /answers/{slug} posts so Google can discover the deep pages via crawls
 * from the shallow ones.
 */
export function InternalLinkNetwork({
  seoTrade,
  cityLabel,
  focusText,
  heading,
}: Props) {
  const hub = hubForSeoTrade(seoTrade);
  const answers = relatedAnswersFor(seoTrade, focusText, 4);
  const siblings = otherHubs(hub?.slug).slice(0, 3);

  if (!hub && answers.length === 0) return null;

  return (
    <section
      aria-label="Related resources"
      className="mt-12 rounded-2xl border border-border bg-muted/30 p-6 md:p-8"
    >
      <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
        {heading ?? "Related resources"}
      </p>
      <div className="mt-5 grid gap-8 md:grid-cols-2">
        {hub ? (
          <div>
            <h3 className="font-display text-lg tracking-tight">
              {hub.trade} marketing hub
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              The full playbook we use for {cityLabel} {hub.trade.toLowerCase()}.
            </p>
            <ul className="mt-3 space-y-1.5 text-sm">
              <li>
                <Link
                  to="/hubs/$trade"
                  params={{ trade: hub.slug }}
                  className="underline decoration-muted-foreground/40 hover:decoration-foreground"
                >
                  Visit the {hub.title}
                </Link>
              </li>
              {siblings.map((s) => (
                <li key={s.slug}>
                  <Link
                    to="/hubs/$trade"
                    params={{ trade: s.slug }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {answers.length > 0 ? (
          <div>
            <h3 className="font-display text-lg tracking-tight">
              Answers for {hub ? hub.trade.toLowerCase() : "trade"} owners
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Short, cited answers to the questions {cityLabel} homeowners and
              contractors actually ask.
            </p>
            <ul className="mt-3 space-y-1.5 text-sm">
              {answers.map((a) => (
                <li key={a.slug}>
                  <Link
                    to="/answers/$slug"
                    params={{ slug: a.slug }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {a.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  );
}